import type {
  CSSGridTemplateTemplate,
  CSSGridTemplateTemplates,
} from "@components/Layout/CSSGridTemplate";

import { info } from "./CSSGrid.info";
import type {
  Breakpoint,
  ColumnMax,
  CSSGridProps,
  Reverse,
} from "./CSSGrid.types";

const concat = (...args: CSSGridTemplateTemplate[]): CSSGridTemplateTemplate =>
  ([] as CSSGridTemplateTemplate).concat(...args);

const getDefaultNames = (columns: ColumnMax): CSSGridTemplateTemplate =>
  Array.from(new Array(columns));

const appendDefaultNames = (
  template: CSSGridTemplateTemplate,
  defaultNames: CSSGridTemplateTemplate,
): CSSGridTemplateTemplate =>
  concat(template, defaultNames.slice(template.length));

const prependDefaultNames = (
  template: CSSGridTemplateTemplate,
  defaultNames: CSSGridTemplateTemplate,
): CSSGridTemplateTemplate =>
  concat(
    defaultNames.slice(0, defaultNames.length - template.length),
    template,
  );

export const getMaxColumns = (breakpoint: Breakpoint): ColumnMax => {
  return info.maxColumns[breakpoint];
};

const normalizeTemplate = (
  templateNames: CSSGridTemplateTemplate[],
  breakpoint: Breakpoint,
  reverse: Reverse,
): CSSGridTemplateTemplate[] => {
  const maxColumns = getMaxColumns(breakpoint);
  const defaultNames = getDefaultNames(maxColumns);

  return templateNames.map(names => {
    if (names.length < maxColumns) {
      return reverse
        ? prependDefaultNames(names, defaultNames)
        : appendDefaultNames(names, defaultNames);
    } else if (names.length > maxColumns) {
      console.error(
        `The number of columns exceeds the maximum value: ${maxColumns}`,
      );
    }

    return names;
  });
};

const normalizeTemplates = (
  templates: CSSGridTemplateTemplates,
  reverse: Reverse,
): CSSGridTemplateTemplates => {
  const entries = Object.entries(templates).map(([breakpoint, template]) => [
    breakpoint,
    normalizeTemplate(template, breakpoint as Breakpoint, reverse),
  ]);

  return Object.fromEntries(entries);
};

export const normalizeAreas = <T extends CSSGridProps["areas"]>(
  areas: T,
  reverse: Reverse,
): T => {
  const {
    all,
    largeMobile,
    smallTablet,
    tablet,
    smallDesktop,
    desktop,
    largeDesktop,
  } = areas;
  const { AreaComponents, templates } = areas();

  return Object.assign(
    () => ({
      AreaComponents,
      templates: normalizeTemplates(templates, reverse),
    }),
    {
      all,
      largeMobile,
      smallTablet,
      tablet,
      smallDesktop,
      desktop,
      largeDesktop,
    },
  ) as T;
};
